import { useMemo, useState } from "react";
import { Plus, Mail, List, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PageHeader, Empty, SelectField } from "../components/common";
import { RdvItem } from "../components/RdvItem";
import { RdvFormDialog } from "../components/RdvFormDialog";
import { MonthCalendar } from "../components/MonthCalendar";
import { useData, api } from "../context/DataContext";
import { fmtDate, isOverdue } from "../lib/constants";

const FILTERS = {
  avenir: (r) => !r.fait && !isOverdue(r.date),
  retard: (r) => !r.fait && isOverdue(r.date),
  faits: (r) => r.fait,
  tous: () => true,
};

export default function Agenda() {
  const { rdvs } = useData();
  const [view, setView] = useState("liste");
  const [tab, setTab] = useState("avenir");
  const [type, setType] = useState("all");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [sending, setSending] = useState(false);

  const filtered = useMemo(() => rdvs.filter((r) => type === "all" || r.type === type), [rdvs, type]);
  const groups = useMemo(() => {
    const list = filtered.filter(FILTERS[tab]).sort((a, b) => (tab === "faits" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)));
    return list.reduce((acc, r) => { const k = r.date.slice(0, 10); (acc[k] = acc[k] || []).push(r); return acc; }, {});
  }, [filtered, tab]);
  const count = (k) => rdvs.filter(FILTERS[k]).length;
  const openEdit = (item) => setDlg({ open: true, item });

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`Récapitulatif envoyé à ${data.to}`, { description: `${data.jour} action(s) aujourd'hui · ${data.retard} en retard` });
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi du récapitulatif"); }
    setSending(false);
  };

  return (
    <div data-testid="agenda-page">
      <PageHeader eyebrow="Organisation" title="Rendez-vous & relances" description="Planifiez vos rendez-vous clients et vos rappels de relance, cochez-les une fois traités. Un récapitulatif vous est envoyé par e-mail chaque matin (heure modifiable dans Réglages)."
        actions={<>
          <Button variant="outline" onClick={sendDigest} disabled={sending} data-testid="agenda-send-digest-button"><Mail className="mr-1 h-4 w-4" />{sending ? "Envoi…" : "Recevoir le récap du jour"}</Button>
          <Button className="kc-btn-primary" onClick={() => setDlg({ open: true, item: null })} data-testid="agenda-new-button"><Plus className="mr-1 h-4 w-4" />Planifier</Button>
        </>} />
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="bg-[var(--kc-navy)] text-white/70">
              <TabsTrigger value="liste" data-testid="agenda-view-liste"><List className="mr-1.5 h-3.5 w-3.5" />Liste</TabsTrigger>
              <TabsTrigger value="calendrier" data-testid="agenda-view-calendrier"><CalendarDays className="mr-1.5 h-3.5 w-3.5" />Calendrier</TabsTrigger>
            </TabsList>
          </Tabs>
          {view === "liste" && (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-[var(--kc-sand)]">
                <TabsTrigger value="avenir" data-testid="agenda-tab-avenir">À venir ({count("avenir")})</TabsTrigger>
                <TabsTrigger value="retard" data-testid="agenda-tab-retard">En retard ({count("retard")})</TabsTrigger>
                <TabsTrigger value="faits" data-testid="agenda-tab-faits">Traités ({count("faits")})</TabsTrigger>
                <TabsTrigger value="tous" data-testid="agenda-tab-tous">Tous</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
        <div className="w-full md:w-56">
          <SelectField testId="agenda-type-filter" value={type} onChange={setType}
            options={[{ id: "all", label: "Tous les types" }, { id: "rendez-vous", label: "Rendez-vous" }, { id: "relance", label: "Relances" }]} />
        </div>
      </div>

      {view === "calendrier" ? (
        <MonthCalendar rdvs={filtered} onEdit={openEdit} />
      ) : (
        <div className="space-y-8" data-testid="agenda-list-view">
          {Object.entries(groups).map(([day, items]) => (
            <section key={day} className="grid gap-4 md:grid-cols-[180px_1fr]" data-testid={`agenda-day-${day}`}>
              <div>
                <p className="font-display text-lg font-semibold capitalize text-[var(--kc-navy)]">{fmtDate(day, "EEEE")}</p>
                <p className="text-sm text-[var(--kc-muted)]">{fmtDate(day, "d MMMM yyyy")}</p>
              </div>
              <div className="space-y-2.5">{items.map((r) => <RdvItem key={r.id} rdv={r} onEdit={openEdit} />)}</div>
            </section>
          ))}
          {!Object.keys(groups).length && <Empty>Rien à afficher dans cette vue.</Empty>}
        </div>
      )}
      <RdvFormDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} initial={dlg.item} />
    </div>
  );
}
